import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FILE_BASE_URL } from '../../services/api';
import './PatientOrder360Modal.css';

/**
 * Container Cap Color & Styling Resolver (Standard ISO / Clinical Laboratory tubes)
 */
const resolveContainerInfo = (containerName, containerColor) => {
  const cName = (containerName || '').toLowerCase();
  if (cName.includes('yellow') || cName.includes('sst') || cName.includes('gel')) {
    return { dotColor: '#eab308', bg: '#fefce8', border: '#fde047', label: containerName || 'Yellow Top (SST Gel Separator)' };
  }
  if (cName.includes('lavender') || cName.includes('purple') || cName.includes('edta')) {
    return { dotColor: '#a855f7', bg: '#faf5ff', border: '#e9d5ff', label: containerName || 'Lavender Top (K2/K3 EDTA)' };
  }
  if (cName.includes('red') || cName.includes('plain') || cName.includes('clot')) {
    return { dotColor: '#ef4444', bg: '#fef2f2', border: '#fecaca', label: containerName || 'Red Top (Plain / Clot Activator)' };
  }
  if (cName.includes('blue') || cName.includes('citrate') || cName.includes('coag')) {
    return { dotColor: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe', label: containerName || 'Light Blue Top (Sodium Citrate)' };
  }
  if (cName.includes('grey') || cName.includes('gray') || cName.includes('fluoride')) {
    return { dotColor: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', label: containerName || 'Grey Top (Sodium Fluoride)' };
  }
  if (cName.includes('green') || cName.includes('heparin')) {
    return { dotColor: '#22c55e', bg: '#f0fdf4', border: '#bbf7d0', label: containerName || 'Green Top (Sodium Heparin)' };
  }
  if (cName.includes('black') || cName.includes('esr')) {
    return { dotColor: '#1e293b', bg: '#f8fafc', border: '#cbd5e1', label: containerName || 'Black Top (ESR / Citrate)' };
  }
  return {
    dotColor: containerColor || '#64748b',
    bg: '#f8fafc',
    border: '#e2e8f0',
    label: containerName || 'Standard Specimen Tube',
  };
};

/**
 * PATIENT & ORDER 360° VIEW-ONLY MODAL
 * Complete patient, clinical referral, and order audit overview.
 * Read-only enterprise component with full XSS safety and responsive layout.
 */
export const PatientOrder360Modal = ({
  isOpen,
  onClose,
  patientData,
  onPrintBarcode,
}) => {
  const [copiedField, setCopiedField] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (lightboxImage) {
          setLightboxImage(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, lightboxImage]);

  // Copy to clipboard helper
  const handleCopy = useCallback((text, fieldName) => {
    if (!text || text === '—') return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 1800);
    }
  }, []);

  // Safe data extraction
  const patient = patientData || {};
  const samples = Array.isArray(patient.samples)
    ? patient.samples
    : patient.itemName || patient.testId
    ? [patient]
    : [];

  // Derive Patient Photo if available
  const photoUrl =
    patient.photoUrl ||
    patient.patientPhoto ||
    patient.photo ||
    patient.profileImage ||
    patient.patientPhotoUrl ||
    null;

  // Derive Demographics
  const patientName = patient.patientName || 'Unknown Patient';
  const uhid = patient.patientId || '—';
  const labNo = patient.ledgerTransactionNo || patient.labNo || '—';
  const mobile = patient.mobile || patient.contactNo || patient.phone || '';
  const email = patient.email || '';
  const ageGender = patient.ageGender || '';
  const centreName = patient.centreName || 'Main Hospital Laboratory';
  const branchName = patient.branchName || centreName;
  const doctorName =
    patient.doctorName ||
    samples[0]?.doctorName ||
    patient.referringDoctor ||
    'Self / Walk-in';

  // Format Registration Date safely
  const rawDate = patient.registrationDate || patient.date || samples[0]?.date || samples[0]?.registrationDate;
  const formattedRegDate = useMemo(() => {
    if (!rawDate) return '—';
    try {
      const d = new Date(rawDate);
      if (isNaN(d.getTime())) return String(rawDate);
      return d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return String(rawDate);
    }
  }, [rawDate]);

  // Order Metrics Calculations
  const metrics = useMemo(() => {
    const total = samples.length;
    const departments = new Set();
    const specimenTypes = new Set();
    let pendingCount = 0;
    let collectedCount = 0;
    let receivedCount = 0;
    let rejectedCount = 0;

    samples.forEach((s) => {
      if (s.departmentName) departments.add(s.departmentName);
      if (s.sampleTypeName) specimenTypes.add(s.sampleTypeName);

      const status = (s.isSampleCollected || 'N').toUpperCase();
      if (status === 'Y') receivedCount++;
      else if (status === 'S') collectedCount++;
      else if (status === 'R') rejectedCount++;
      else pendingCount++;
    });

    let overallStatus = 'PENDING';
    let overallLabel = 'Pending Collection';
    let statusClass = 'status-pending';

    if (receivedCount === total && total > 0) {
      overallStatus = 'RECEIVED';
      overallLabel = 'All Received in Lab';
      statusClass = 'status-received';
    } else if (collectedCount === total && total > 0) {
      overallStatus = 'COLLECTED';
      overallLabel = 'Fully Collected';
      statusClass = 'status-collected';
    } else if (collectedCount > 0 || receivedCount > 0) {
      overallStatus = 'PARTIAL';
      overallLabel = `In Progress (${collectedCount + receivedCount}/${total})`;
      statusClass = 'status-collected';
    } else if (rejectedCount === total && total > 0) {
      overallStatus = 'REJECTED';
      overallLabel = 'All Rejected';
      statusClass = 'status-rejected';
    }

    return {
      total,
      deptCount: departments.size || 1,
      specimenCount: specimenTypes.size || 1,
      overallStatus,
      overallLabel,
      statusClass,
    };
  }, [samples]);

  // Clinical Remarks / Notes
  const remarksText =
    patient.remarks ||
    patient.comments ||
    patient.doctorRemarks ||
    patient.clinicalHistory ||
    patient.orderRemarks ||
    samples[0]?.remarks ||
    samples[0]?.doctorRemarks ||
    null;

  // Documents / Attachments
  const documentsList = useMemo(() => {
    if (Array.isArray(patient.documents) && patient.documents.length > 0) return patient.documents;
    if (Array.isArray(patient.attachments) && patient.attachments.length > 0) return patient.attachments;
    if (Array.isArray(patient.files) && patient.files.length > 0) return patient.files;
    return [];
  }, [patient]);

  if (!isOpen || !patientData) return null;

  return (
    <div className="po360-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="po360-modal" onClick={(e) => e.stopPropagation()}>
        {/* ── Header ── */}
        <header className="po360-header">
          <div className="po360-header-info">
            <div className="po360-header-icon" aria-hidden="true">
              📋
            </div>
            <div>
              <h2 className="po360-title">PATIENT & ORDER INFORMATION</h2>
              <p className="po360-subtitle">Complete patient, registration and order overview</p>
            </div>
          </div>

          <div className="po360-header-actions">
            {labNo !== '—' && (
              <span className="po360-header-badge" title="Lab Registration Number">
                Lab No: {labNo}
              </span>
            )}
            <button
              type="button"
              className="po360-close-btn"
              onClick={onClose}
              title="Close modal (Esc)"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>
        </header>

        {/* ── Scrollable Body ── */}
        <div className="po360-body">
          {/* ── Section 1: Patient Identity Card ── */}
          <section className="po360-identity-card" aria-label="Patient Identity">
            <div className="po360-photo-container">
              {photoUrl ? (
                <div
                  className="po360-photo-img-wrap"
                  onClick={() => {
                    setLightboxImage(photoUrl);
                    setZoomLevel(1);
                  }}
                  title="Click to enlarge patient photo"
                >
                  <img
                    src={photoUrl}
                    alt={`Photo of ${patientName}`}
                    className="po360-photo-img"
                  />
                  <div className="po360-photo-zoom-hint">🔍 Zoom</div>
                </div>
              ) : (
                <div className="po360-photo-placeholder" title="No patient photo available">
                  <span className="po360-photo-placeholder-icon">👤</span>
                  <span className="po360-photo-placeholder-caption">No photo</span>
                </div>
              )}
            </div>

            <div className="po360-identity-main">
              <div className="po360-name-row">
                <span className="po360-patient-name">{patientName}</span>
                {(patient.priority === 'Urgent' || patient.isUrgent) && (
                  <span className="po360-pill po360-pill-urgent">🔴 URGENT</span>
                )}
              </div>

              <div className="po360-badges-row">
                {ageGender && (
                  <span className="po360-pill po360-pill-gender">
                    {ageGender}
                  </span>
                )}

                <span className={`po360-pill po360-pill-status ${metrics.statusClass}`}>
                  ● {metrics.overallLabel}
                </span>

                {samples.some((s) => s.isOutsource || s.processingMode === 'OUTSOURCE') && (
                  <span className="po360-pill po360-pill-outsource">
                    🌐 Outsource Referred
                  </span>
                )}
              </div>

              <div className="po360-ids-row">
                <div className="po360-id-chip">
                  <span className="po360-id-chip-lbl">UHID</span>
                  <span className="po360-id-chip-val">{uhid}</span>
                  {uhid !== '—' && (
                    <button
                      type="button"
                      className="po360-copy-btn"
                      onClick={() => handleCopy(uhid, 'UHID')}
                      title="Copy UHID"
                    >
                      {copiedField === 'UHID' ? '✓' : '📋'}
                    </button>
                  )}
                </div>

                <div className="po360-id-chip">
                  <span className="po360-id-chip-lbl">Lab No</span>
                  <span className="po360-id-chip-val">{labNo}</span>
                  {labNo !== '—' && (
                    <button
                      type="button"
                      className="po360-copy-btn"
                      onClick={() => handleCopy(labNo, 'LabNo')}
                      title="Copy Lab No"
                    >
                      {copiedField === 'LabNo' ? '✓' : '📋'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* ── Section 2: Two-Column Information Cards ── */}
          <section className="po360-info-grid" aria-label="Patient and Registration Details">
            {/* Card A: Personal & Contact */}
            <article className="po360-card">
              <header className="po360-card-header">
                <span className="po360-card-header-icon">👤</span>
                <h3 className="po360-card-title">Personal & Contact Information</h3>
              </header>

              <div className="po360-kv-list">
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Full Name</span>
                  <span className="po360-kv-val">{patientName}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Age / Gender</span>
                  <span className="po360-kv-val">{ageGender || '—'}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">UHID (Patient ID)</span>
                  <span className="po360-kv-val font-mono">{uhid}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Contact Mobile</span>
                  <span className="po360-kv-val">
                    {mobile ? (
                      <a href={`tel:${mobile}`} className="po360-phone-link">
                        📞 {mobile}
                      </a>
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Email Address</span>
                  <span className="po360-kv-val">{email || '—'}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Address / Location</span>
                  <span className="po360-kv-val">{patient.address || patient.city || '—'}</span>
                </div>
              </div>
            </article>

            {/* Card B: Registration & Clinical Referral */}
            <article className="po360-card">
              <header className="po360-card-header">
                <span className="po360-card-header-icon">🏥</span>
                <h3 className="po360-card-title">Registration & Clinical Referral</h3>
              </header>

              <div className="po360-kv-list">
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Lab Registration</span>
                  <span className="po360-kv-val font-mono">{labNo}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Registration Date</span>
                  <span className="po360-kv-val">{formattedRegDate}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Centre / Facility</span>
                  <span className="po360-kv-val">{centreName}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Branch</span>
                  <span className="po360-kv-val">{branchName}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Referring Doctor</span>
                  <span className="po360-kv-val">{doctorName}</span>
                </div>
                <div className="po360-kv-row">
                  <span className="po360-kv-key">Order Priority</span>
                  <span className="po360-kv-val">
                    {patient.priority === 'Urgent' || patient.isUrgent ? '🔴 Urgent' : 'Routine'}
                  </span>
                </div>
              </div>
            </article>
          </section>

          {/* ── Section 3: Order Summary KPI Metric Tiles ── */}
          <section className="po360-kpi-bar" aria-label="Order Metrics">
            <div className="po360-kpi-tile kpi-tests">
              <div className="po360-kpi-icon">🧪</div>
              <div className="po360-kpi-info">
                <span className="po360-kpi-label">Tests Ordered</span>
                <span className="po360-kpi-val">{metrics.total}</span>
              </div>
            </div>

            <div className="po360-kpi-tile kpi-depts">
              <div className="po360-kpi-icon">🏢</div>
              <div className="po360-kpi-info">
                <span className="po360-kpi-label">Departments</span>
                <span className="po360-kpi-val">{metrics.deptCount}</span>
              </div>
            </div>

            <div className="po360-kpi-tile kpi-specs">
              <div className="po360-kpi-icon">🩸</div>
              <div className="po360-kpi-info">
                <span className="po360-kpi-label">Specimens</span>
                <span className="po360-kpi-val">{metrics.specimenCount}</span>
              </div>
            </div>

            <div className="po360-kpi-tile kpi-stage">
              <div className="po360-kpi-icon">⏱️</div>
              <div className="po360-kpi-info">
                <span className="po360-kpi-label">Order Stage</span>
                <span className="po360-kpi-val" style={{ fontSize: '0.88rem' }}>
                  {metrics.overallStatus}
                </span>
              </div>
            </div>
          </section>

          {/* ── Section 4: Ordered Investigations Table ── */}
          <section className="po360-tests-card" aria-label="Ordered Investigations">
            <div className="po360-tests-header">
              <h3 className="po360-tests-title">
                <span>📋 Ordered Investigations & Tests</span>
              </h3>
              <span className="po360-tests-count">
                {samples.length} item{samples.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="po360-table-wrap">
              <table className="po360-table">
                <thead>
                  <tr>
                    <th className="po360-td-sno">#</th>
                    <th>Investigation / Test</th>
                    <th>Department</th>
                    <th>Specimen & Container</th>
                    <th>Barcode / SIN</th>
                    <th>Status</th>
                    <th>Collection Audit</th>
                    <th>Receive Audit</th>
                  </tr>
                </thead>
                <tbody>
                  {samples.length === 0 ? (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
                        No investigations recorded for this patient.
                      </td>
                    </tr>
                  ) : (
                    samples.map((s, idx) => {
                      const tubeInfo = resolveContainerInfo(s.containerName, s.containerColor);
                      const isReceived = s.isSampleCollected === 'Y';
                      const isCollected = s.isSampleCollected === 'S';
                      const isRejected = s.isSampleCollected === 'R';
                      const isOutsource = Boolean(s.isOutsource || s.processingMode === 'OUTSOURCE' || s.referenceLabName);

                      return (
                        <tr key={s.testId || idx}>
                          <td className="po360-td-sno">{idx + 1}</td>
                          <td>
                            <div>
                              <span className="po360-test-name">
                                {s.itemName || s.investigationName || s.testName || 'Standard Investigation'}
                              </span>
                              {s.itemCode && <span className="po360-item-code">({s.itemCode})</span>}
                            </div>
                            {isOutsource && (
                              <span className="po360-outsource-badge">
                                🌐 OUTSOURCE • {s.referenceLabName || 'Reference Lab'}
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="po360-dept-pill">
                              {s.departmentName || s.subCategoryName || 'Biochemistry'}
                            </span>
                          </td>
                          <td>
                            <div className="po360-specimen-cell">
                              <span
                                className="po360-swatch-box"
                                style={{ backgroundColor: tubeInfo.dotColor, borderColor: tubeInfo.border }}
                                title={`${tubeInfo.label}${s.containerName ? ` (${s.containerName})` : ''}`}
                                aria-label={tubeInfo.label}
                              />
                              <span>{s.sampleTypeName || 'Standard Specimen'}</span>
                            </div>
                          </td>
                          <td>
                            {s.barcodeNo ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span className="po360-barcode-pill">
                                  🏷️ {s.barcodeNo}
                                </span>
                                {onPrintBarcode && (
                                  <button
                                    type="button"
                                    onClick={() => onPrintBarcode(s)}
                                    title="Print Barcode Label"
                                    style={{
                                      background: 'transparent',
                                      border: 'none',
                                      cursor: 'pointer',
                                      fontSize: '0.85rem',
                                    }}
                                  >
                                    🖨️
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span style={{ color: '#cbd5e1' }}>—</span>
                            )}
                          </td>
                          <td>
                            <span
                              className={`po360-pill po360-pill-status ${
                                isReceived
                                  ? 'status-received'
                                  : isCollected
                                  ? 'status-collected'
                                  : isRejected
                                  ? 'status-rejected'
                                  : 'status-pending'
                              }`}
                            >
                              {isReceived
                                ? 'RECEIVED'
                                : isCollected
                                ? 'COLLECTED'
                                : isRejected
                                ? 'REJECTED'
                                : 'PENDING'}
                            </span>
                          </td>
                          <td>
                            {s.sampleCollector || s.sampleCollectionDate ? (
                              <div className="po360-audit-cell">
                                <span className="po360-audit-user">
                                  By: {s.sampleCollector || 'Staff'}
                                </span>
                                <br />
                                <span className="po360-audit-time">
                                  {s.sampleCollectionDate
                                    ? new Date(s.sampleCollectionDate).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '—'}
                                </span>
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>⏳ Awaiting Draw</span>
                            )}
                          </td>
                          <td>
                            {s.sampleReceiver || s.sampleReceiveDate ? (
                              <div className="po360-audit-cell">
                                <span className="po360-audit-user">
                                  Rcvd: {s.sampleReceiver || 'Staff'}
                                </span>
                                <br />
                                <span className="po360-audit-time">
                                  {s.sampleReceiveDate
                                    ? new Date(s.sampleReceiveDate).toLocaleString('en-IN', {
                                        day: '2-digit',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })
                                    : '—'}
                                </span>
                              </div>
                            ) : s.isSampleCollected === 'S' ? (
                              <span style={{ fontSize: '0.74rem', color: '#d97706' }}>⏳ In Transit / Lab</span>
                            ) : (
                              <span style={{ fontSize: '0.74rem', color: '#cbd5e1' }}>—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Section 5 & 6: Remarks & Documents Grid ── */}
          <section className="po360-meta-sections-grid" aria-label="Clinical Remarks and Documents">
            {/* Remarks Card */}
            <article className="po360-card">
              <header className="po360-card-header">
                <span className="po360-card-header-icon">💬</span>
                <h3 className="po360-card-title">Clinical Remarks / Notes</h3>
              </header>

              {remarksText ? (
                <div className="po360-callout-box">
                  {remarksText}
                </div>
              ) : (
                <div className="po360-empty-notice">
                  <span>ℹ️</span>
                  <span>No remarks or special instructions recorded for this order.</span>
                </div>
              )}
            </article>

            {/* Documents & Attachments Card */}
            <article className="po360-card">
              <header className="po360-card-header">
                <span className="po360-card-header-icon">📎</span>
                <h3 className="po360-card-title">Documents & Attachments</h3>
              </header>

              {documentsList.length > 0 ? (
                <div className="po360-docs-list">
                  {documentsList.map((doc, dIdx) => {
                    const docName = typeof doc === 'string' ? doc : doc.name || doc.fileName || `Attachment-${dIdx + 1}`;
                    const rawUrl = typeof doc === 'string' ? doc : doc.viewUrl || doc.downloadUrl || doc.url || doc.filePath || null;
                    const docUrl = rawUrl
                      ? (rawUrl.startsWith('http') || rawUrl.startsWith('blob:') || rawUrl.startsWith('data:') ? rawUrl : `${FILE_BASE_URL}${rawUrl}`)
                      : null;

                    return (
                      <div key={dIdx} className="po360-doc-item">
                        <div className="po360-doc-info">
                          <span>📄</span>
                          <span className="po360-doc-name" title={docName}>
                            {docName}
                          </span>
                        </div>
                        {docUrl && (
                          <button
                            type="button"
                            className="po360-doc-action-btn"
                            onClick={() => {
                              if (docUrl.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
                                setLightboxImage(docUrl);
                                setZoomLevel(1);
                              } else {
                                window.open(docUrl, '_blank', 'noopener,noreferrer');
                              }
                            }}
                          >
                            Preview
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="po360-empty-notice">
                  <span>ℹ️</span>
                  <span>No referral documents or clinical files attached.</span>
                </div>
              )}
            </article>
          </section>
        </div>

        {/* ── Footer ── */}
        <footer className="po360-footer">
          <div className="po360-footer-note">
            <span>🔒</span>
            <span>View-Only Clinical Audit Mode</span>
          </div>

          <button type="button" className="po360-btn-close" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>

      {/* ── Photo / Document Preview Lightbox Overlay ── */}
      {lightboxImage && (
        <div
          className="po360-lightbox-overlay"
          onClick={() => setLightboxImage(null)}
          role="dialog"
          aria-label="Image Preview Lightbox"
        >
          <div className="po360-lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightboxImage}
              alt="Enlarged Preview"
              className="po360-lightbox-img"
              style={{ transform: `scale(${zoomLevel})` }}
            />

            <div className="po360-lightbox-toolbar">
              <button
                type="button"
                className="po360-lightbox-btn"
                onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 3))}
                title="Zoom In"
              >
                🔍+
              </button>
              <button
                type="button"
                className="po360-lightbox-btn"
                onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.5))}
                title="Zoom Out"
              >
                🔍-
              </button>
              <button
                type="button"
                className="po360-lightbox-btn"
                onClick={() => setZoomLevel(1)}
                title="Fit to Screen"
              >
                Fit
              </button>
              <button
                type="button"
                className="po360-lightbox-btn"
                onClick={() => setLightboxImage(null)}
                title="Close Lightbox"
              >
                ✕ Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientOrder360Modal;
